import React, { useEffect, useRef, useState } from "react";

/**
 * TapDiagOverlay — real-iPad-Safari isolation harness.
 *
 * User reported that `?tapdiag=1` (previous full overlay) makes FINISH
 * ASSEMBLY / COMPLETE TESTING WORK on iPad Safari, while normal mode fails.
 * This file now supports multiple MODES so we can find the MINIMUM piece of
 * the overlay that flips the behaviour. Nothing here should stay in
 * production — this is diagnostic-only.
 *
 * Enable with `?tapdiag=<mode>` (persisted in localStorage across the login
 * redirect). Modes:
 *
 *   off                  — no overlay, no listeners (baseline, broken on iPad)
 *   full                 — legacy full overlay (rendering + polling + all listeners) — WORKS
 *   B  / pointer         — only `pointerdown` + `pointerup` on window, no render, no polling
 *   C  / touch           — only `touchstart` + `touchend` on window, no render, no polling
 *   D  / click           — only `click` on window, no render, no polling
 *   E  / poll            — only `setInterval(elementsFromPoint, 1500)`, no listeners, no render
 *   F  / render          — only render the fixed panel, no listeners, no polling
 *   touchstart           — ONLY `touchstart` on window (subset of C)
 *   touchend             — ONLY `touchend` on window (subset of C)
 *   doc-touchstart       — `touchstart` on `document` (not window)
 *   passive-false        — `touchstart` on window with `{passive:false}`
 *   non-capture          — `touchstart` on window with `{capture:false}`
 *
 * A tiny 30-px badge at the bottom of the screen shows the active mode so
 * you know exactly which variant is loaded, and provides ← → arrows to hop
 * to the next / previous variant without typing URLs.
 */

const MODES = [
  "off",
  "render",       // F
  "body",         // observe body/html state without polling elementsFromPoint
  "poll",         // E
  "click",        // D
  "pointer",      // B
  "touch",        // C
  "touchstart",
  "touchend",
  "doc-touchstart",
  "passive-false",
  "non-capture",
  "full",
];

const ALIASES = {
  "1": "full",
  "0": "off",
  "A": "off",
  "B": "pointer",
  "C": "touch",
  "D": "click",
  "E": "poll",
  "F": "render",
};

function resolveMode(raw) {
  if (!raw) return "off";
  const v = String(raw);
  if (ALIASES[v]) return ALIASES[v];
  if (MODES.includes(v)) return v;
  return "off";
}

export function TapDiagOverlay({ mode: rawMode }) {
  const mode = resolveMode(rawMode);
  const [logs, setLogs] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [bodyState, setBodyState] = useState(null);
  const bufRef = useRef([]);

  const push = (entry) => {
    const stamp = new Date().toISOString().slice(11, 23);
    bufRef.current = [{ t: stamp, ...entry }, ...bufRef.current].slice(0, 30);
    setLogs(bufRef.current);
  };

  const describe = (el) => {
    if (!el) return "(null)";
    const id = el.id ? `#${el.id}` : "";
    const cls = (el.className && typeof el.className === "string")
      ? "." + el.className.trim().split(/\s+/).slice(0, 3).join(".")
      : "";
    const tid = el.getAttribute && el.getAttribute("data-testid");
    return `${el.tagName?.toLowerCase() || "?"}${id}${cls}${tid ? "[" + tid + "]" : ""}`.slice(0, 100);
  };

  // ————— Effect: attach ONLY the listeners appropriate for the current mode
  useEffect(() => {
    if (mode === "off") return;

    // What kinds do we attach on WINDOW (or document) with what options?
    let toAttach = []; // { target, kind, opts }

    const wOpts = { capture: true, passive: true };

    switch (mode) {
      case "full":
        toAttach = [
          { target: window, kind: "pointerdown", opts: wOpts },
          { target: window, kind: "pointerup",   opts: wOpts },
          { target: window, kind: "touchstart",  opts: wOpts },
          { target: window, kind: "touchend",    opts: wOpts },
          { target: window, kind: "click",       opts: wOpts },
        ];
        break;
      case "pointer":
        toAttach = [
          { target: window, kind: "pointerdown", opts: wOpts },
          { target: window, kind: "pointerup",   opts: wOpts },
        ];
        break;
      case "touch":
        toAttach = [
          { target: window, kind: "touchstart", opts: wOpts },
          { target: window, kind: "touchend",   opts: wOpts },
        ];
        break;
      case "click":
        toAttach = [{ target: window, kind: "click", opts: wOpts }];
        break;
      case "touchstart":
        toAttach = [{ target: window, kind: "touchstart", opts: wOpts }];
        break;
      case "touchend":
        toAttach = [{ target: window, kind: "touchend", opts: wOpts }];
        break;
      case "doc-touchstart":
        toAttach = [{ target: document, kind: "touchstart", opts: wOpts }];
        break;
      case "passive-false":
        toAttach = [{ target: window, kind: "touchstart", opts: { capture: true, passive: false } }];
        break;
      case "non-capture":
        toAttach = [{ target: window, kind: "touchstart", opts: { capture: false, passive: true } }];
        break;
      case "render":
      case "poll":
      default:
        toAttach = [];
    }

    const attached = toAttach.map(({ target, kind, opts }) => {
      const h = (e) => {
        const t = e.target;
        const btn = t && (t.closest?.('[data-testid="finish-assembly-button"]')
                       || t.closest?.('[data-testid="complete-testing-button"]'));
        if (!btn) return;
        push({
          kind,
          which: btn.getAttribute("data-testid"),
          target: describe(t),
          disabled: btn.disabled,
          defaultPrevented: e.defaultPrevented,
          x: e.clientX, y: e.clientY,
        });
      };
      target.addEventListener(kind, h, opts);
      return { target, kind, h, opts };
    });

    // Polling
    let snapId = null;
    if (mode === "full" || mode === "poll" || mode === "body" || mode === "render") {
      const snap = () => {
        // ————— Body / html state observer (the actual smoking gun for
        // Radix Dialog residue on iPad Safari) —————
        const body = document.body;
        const html = document.documentElement;
        const portalCount = document.querySelectorAll('[data-radix-portal],[data-radix-focus-guard]').length;
        const overlayCount = document.querySelectorAll('[data-radix-dialog-overlay],[data-state="open"][role="dialog"]').length;
        setBodyState({
          bodyInlinePointerEvents: body.style.pointerEvents || "(none-inline)",
          bodyComputedPointerEvents: window.getComputedStyle(body).pointerEvents,
          bodyInlineOverflow: body.style.overflow || "(none-inline)",
          bodyInlinePaddingRight: body.style.paddingRight || "(none-inline)",
          bodyDataScrollLocked: body.getAttribute("data-scroll-locked") || "(unset)",
          htmlInlineOverflow: html.style.overflow || "(none-inline)",
          htmlDataScrollLocked: html.getAttribute("data-scroll-locked") || "(unset)",
          orphanPortals: portalCount,
          openDialogs: overlayCount,
          bodyClass: (body.className || "").slice(0, 120),
        });

        if (mode === "full" || mode === "poll") {
          const targets = [
            document.querySelector('[data-testid="finish-assembly-button"]'),
            document.querySelector('[data-testid="complete-testing-button"]'),
          ].filter(Boolean);
          setPinned(targets.map((btn) => {
            const r = btn.getBoundingClientRect();
            const cx = r.left + r.width / 2;
            const cy = r.top + r.height / 2;
            const list = (document.elementsFromPoint(cx, cy) || []).slice(0, 5).map(describe);
            const cs = window.getComputedStyle(btn);
            return {
              which: btn.getAttribute("data-testid"),
              disabled: btn.disabled,
              pointerEvents: cs.pointerEvents,
              touchAction: cs.touchAction,
              opacity: cs.opacity,
              rect: { x: Math.round(r.left), y: Math.round(r.top), w: Math.round(r.width), h: Math.round(r.height) },
              cx: Math.round(cx), cy: Math.round(cy),
              topFive: list,
            };
          }));
        }
      };
      snap();
      snapId = setInterval(snap, 1000);
    }

    return () => {
      attached.forEach(({ target, kind, h, opts }) => target.removeEventListener(kind, h, opts));
      if (snapId) clearInterval(snapId);
    };
  }, [mode]);

  // Never render for "off"
  if (mode === "off") return null;

  const rendered = mode === "full" || mode === "render" || mode === "poll" || mode === "pointer" || mode === "touch" || mode === "click" || mode === "touchstart" || mode === "touchend" || mode === "doc-touchstart" || mode === "passive-false" || mode === "non-capture";
  // (rendered = true for all modes except off — badge is essential UI)

  const setMode = (nextMode) => {
    try { localStorage.setItem("tapdiag", nextMode); } catch {}
    const u = new URL(window.location.href);
    u.searchParams.set("tapdiag", nextMode);
    window.location.href = u.toString();
  };
  const idx = MODES.indexOf(mode);
  const prevMode = MODES[(idx - 1 + MODES.length) % MODES.length];
  const nextMode = MODES[(idx + 1) % MODES.length];

  return (
    <div
      style={{
        position: "fixed",
        left: 0, right: 0, bottom: 0,
        zIndex: 2147483000,
        pointerEvents: "none",
        fontFamily: "ui-monospace,Menlo,Monaco,monospace",
        color: "#e5e7eb",
      }}
      data-testid="tap-diag-overlay"
    >
      {/* Compact mode badge — always visible so you know what variant is active. */}
      <div
        style={{
          margin: "0 auto",
          maxWidth: 720,
          background: "rgba(6,8,12,0.92)",
          borderTop: "1px solid rgba(0,229,255,0.5)",
          borderRadius: "8px 8px 0 0",
          padding: 8,
          fontSize: 10,
          lineHeight: 1.35,
          maxHeight: "38vh",
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          pointerEvents: "auto", // ONLY the panel itself, wrapper is none
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
          <span style={{ color: "#00e5ff", fontWeight: 700, letterSpacing: 1.5 }}>TAP-DIAG</span>
          <span style={{ padding: "2px 8px", border: "1px solid #00e5ff", borderRadius: 4, color: "#00e5ff" }}>mode = {mode}</span>
          <button data-testid="tap-diag-prev" onClick={() => setMode(prevMode)}
            style={{ padding: "2px 6px", border: "1px solid #ffffff33", borderRadius: 4, background: "transparent", color: "#e5e7eb", fontSize: 10 }}>
            ← {prevMode}
          </button>
          <button data-testid="tap-diag-next" onClick={() => setMode(nextMode)}
            style={{ padding: "2px 6px", border: "1px solid #ffffff33", borderRadius: 4, background: "transparent", color: "#e5e7eb", fontSize: 10 }}>
            {nextMode} →
          </button>
          <button data-testid="tap-diag-off" onClick={() => setMode("off")}
            style={{ marginLeft: "auto", padding: "2px 8px", border: "1px solid #ff5555", borderRadius: 4, background: "transparent", color: "#ff8888", fontSize: 10 }}>
            OFF
          </button>
        </div>

        {bodyState && (
          <div style={{ borderTop: "1px dashed #ffffff18", marginTop: 4, paddingTop: 4 }}>
            <div style={{ color: "#ff8888" }}>document.body / html state (Radix leak check)</div>
            <div style={{ opacity: 0.9 }}>
              body pointer-events (inline)=<b style={{ color: bodyState.bodyInlinePointerEvents === "none" ? "#ff5555" : "#7fffd4" }}>{bodyState.bodyInlinePointerEvents}</b>
              {" • "}(computed)={bodyState.bodyComputedPointerEvents}
            </div>
            <div style={{ opacity: 0.75 }}>
              body overflow={bodyState.bodyInlineOverflow} • body paddingRight={bodyState.bodyInlinePaddingRight} • body data-scroll-locked={bodyState.bodyDataScrollLocked}
            </div>
            <div style={{ opacity: 0.75 }}>
              html overflow={bodyState.htmlInlineOverflow} • html data-scroll-locked={bodyState.htmlDataScrollLocked}
            </div>
            <div style={{ opacity: 0.75 }}>
              orphan radix portals/focus-guards={bodyState.orphanPortals} • open dialogs={bodyState.openDialogs}
            </div>
            <div style={{ opacity: 0.55 }}>body.class="{bodyState.bodyClass}"</div>
          </div>
        )}

        {(mode === "full" || mode === "poll") && (
          <div>
            {pinned.map((p) => (
              <div key={p.which} style={{ borderTop: "1px dashed #ffffff18", marginTop: 4, paddingTop: 4 }}>
                <div style={{ color: "#00e5ff" }}>{p.which}</div>
                <div style={{ opacity: 0.75 }}>
                  disabled={String(p.disabled)} • pe={p.pointerEvents} • ta={p.touchAction} • op={p.opacity} • ({p.rect.x},{p.rect.y},{p.rect.w}×{p.rect.h}) • c=({p.cx},{p.cy})
                </div>
                <ol style={{ margin: 0, paddingLeft: 18 }}>
                  {p.topFive.map((el, i) => <li key={i} style={{ opacity: i === 0 ? 1 : 0.7 }}>{el}</li>)}
                </ol>
              </div>
            ))}
          </div>
        )}

        <div style={{ borderTop: "1px dashed #ffffff18", marginTop: 6, paddingTop: 4 }}>
          <div style={{ color: "#00e5ff", marginBottom: 2 }}>events on target buttons</div>
          {logs.length === 0
            ? <div style={{ opacity: 0.5 }}>— none yet — tap a button —</div>
            : logs.map((l, i) => (
                <div key={i} style={{ opacity: 0.9 }}>
                  <span style={{ opacity: 0.55 }}>{l.t}</span>{" "}
                  <span style={{ color: "#a1e0ff" }}>{l.kind}</span>{" "}
                  <span style={{ color: "#ffb86b" }}>{l.which}</span>{" "}
                  ({Math.round(l.x||0)},{Math.round(l.y||0)}) disabled={String(l.disabled)}
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
